import {
  Button,
  Collapse,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryState } from 'nuqs';
import { ChangeEvent, useCallback, useState } from 'react';
import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';

import { PromoCode } from '../../types';

type IEndAdornmentProps = {
  appliedPromoCode?: PromoCode;
  isLoading?: boolean;
  promoText: string;
};
const getEndAdornment = ({
  isLoading,
  appliedPromoCode,
  promoText,
}: IEndAdornmentProps): JSX.Element | null => {
  if (isLoading) {
    return <Iconify icon="line-md:loading-loop" width={22} height={22} color="yellow" />;
  }

  if (promoText !== '') {
    return (
      <Iconify
        icon={appliedPromoCode ? 'line-md:confirm' : 'line-md:close-small'}
        width={30}
        height={30}
        color={appliedPromoCode ? 'green' : 'red'}
      />
    );
  }

  return null;
};

type IProps = {
  appliedPromoCode?: PromoCode;
  isLoading?: boolean;
  error?: Error;
};

export default function EventPromoCode({ appliedPromoCode, isLoading, error }: IProps) {
  const [showPromoCode, setShowPromoCode] = useState(false);

  const [currentPromo, setCurrentPromoCode] = useQueryState('promo', { defaultValue: '' });
  const { t } = useTranslate('common');
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPromoCode(event.target.value);
  };

  const onShowPromoCode = useCallback(() => {
    setShowPromoCode(true);
  }, []);

  return (
    <>
      {showPromoCode || currentPromo !== '' ? (
        <Stack gap={error ? 1 : 0}>
          <TextField
            value={currentPromo}
            onChange={handleChange}
            variant="outlined"
            focused
            fullWidth
            sx={{
              pl: 0,
              '&.MuiTextField-root': {
                border: 'none',
              },
              '&:focus-visible': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1, fontSize: 13 }}>
                  {t('order.promo-code')}
                </InputAdornment>
              ),
              endAdornment: getEndAdornment({
                isLoading,
                appliedPromoCode,
                promoText: currentPromo,
              }),
            }}
            error={!!error}
          />
          <Collapse
            in={!!error}
            timeout={{ appear: 300, enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {error?.message}
            </Typography>
          </Collapse>
          <Divider sx={{ paddingTop: '16px' }} />
        </Stack>
      ) : (
        <Button
          variant="text"
          // size="large"
          startIcon={<Iconify icon="solar:ticket-sale-broken" width={15} height={15} />}
          sx={{
            borderRadius: 1,
            p: 0,
            backgroundColor: 'transparent',
          }}
          onClick={onShowPromoCode}
        >
          {t('order.add-promo')}
        </Button>
      )}
    </>
  );
}
